import type {
  ReplicationOptions,
  RxCollection,
} from 'rxdb';
import { type RxReplicationState, replicateRxCollection } from 'rxdb/plugins/replication';
import { from } from 'rxjs';

import convertBooleanIntegersToBooleans from '../database/internals/convertBooleanIntegersToBooleans';
import getBooleanTransformer from '../database/internals/middleware/upsertTransformers/getBooleanTransformer';
import getUnknownRootKeysTransformer from '../database/internals/middleware/upsertTransformers/getUnknownRootKeysTransformer';
import { OfflineTrackedBooksLoadingState } from '../types';
import { DatabaseCollectionNamesToDocType, DatabaseUpsertTransformer } from '../types/database';
// eslint-disable-next-line import/no-cycle
import { ReadwiseFetchNetworkConnectivityError } from '../utils/Errors';
// eslint-disable-next-line import/no-cycle
import exceptionHandler from '../utils/exceptionHandler.platform';
import getServerBaseUrl from '../utils/getServerBaseUrl.platform';
import makeLogger from '../utils/makeLogger';
import requestWithAuth from '../utils/requestWithAuth.platformIncludingExtension';
import urlJoin from '../utils/urlJoin';
import foreground from './portalGates/toForeground';

const logger = makeLogger(__filename, { shouldLogToNewRelic: true });

type Checkpoint = {
  id: string;
  updatedAt: string;
};

// Mostly following this guide https://rxdb.info/replication-http.html
export class CollectionSyncer<T extends keyof DatabaseCollectionNamesToDocType> {
  collection: RxCollection<DatabaseCollectionNamesToDocType[T]>;
  endpointPrefix: string;
  replicationState?: RxReplicationState<DatabaseCollectionNamesToDocType[T], Checkpoint>;
  upsertTransformers: DatabaseUpsertTransformer;

  constructor(collection: RxCollection<DatabaseCollectionNamesToDocType[T]>, endpointPrefix: string) {
    this.collection = collection;
    this.endpointPrefix = endpointPrefix;

    // Compose transformers: strip unknown fields first, then convert booleans
    // Unknown fields must be stripped so the server can add new fields without breaking old clients
    // (RxDB enforces additionalProperties: false at schema root level)
    const unknownKeysTransformer = getUnknownRootKeysTransformer(collection);
    const booleanTransformer = getBooleanTransformer(collection);
    this.upsertTransformers = (plainData: object) => {
      unknownKeysTransformer(plainData);
      booleanTransformer(plainData);
    };
  }

  async requestWithAuth(input: RequestInfo, init?: RequestInit): ReturnType<typeof requestWithAuth> {
    // Helper just so we can stub this for tests
    return requestWithAuth(input, init);
  }

  beginSyncing(params: Partial<ReplicationOptions<DatabaseCollectionNamesToDocType[T], Checkpoint>> = {}, initialSync = false): RxReplicationState<DatabaseCollectionNamesToDocType[T], Checkpoint> {
    // Here we should fetch the tracked books from API
    if (initialSync) {
      foreground.updateCurrentOfflineTrackedBooksLoadingState(OfflineTrackedBooksLoadingState.DownloadingDocuments);
    } else {
      foreground.updateCurrentOfflineTrackedBooksLoadingState(OfflineTrackedBooksLoadingState.Done);
    }
    this.replicationState = replicateRxCollection<DatabaseCollectionNamesToDocType[T], Checkpoint>({
      collection: this.collection,
      replicationIdentifier: `${this.collection.name}-${getServerBaseUrl()}`,
      retryTime: 5000,
      push: {
        handler: async (changedRows) => {
          logger.debug('pushing changed rows ', { changedRows });
          const url = urlJoin([getServerBaseUrl(), `${this.endpointPrefix}`, 'push']);
          const res = await this.requestWithAuth(url, {
            method: 'POST',
            body: JSON.stringify(changedRows),
          });

        /*
          In the documentation you might see this called "conflicts"
          however in reality this is an array of all changed documents that the replication engine then
          uses to determine the new "assumed master schema"
         */
          const modifiedDocuments = await res.json();
          logger.debug('modified documents ', modifiedDocuments);
          for (const item of modifiedDocuments) {
            this.upsertTransformers(item);
          }
          return modifiedDocuments;
        },
        modifier: (data) => {
          convertBooleanIntegersToBooleans(this.collection, data);
          return data;
        },
        batchSize: 10,
      },
      pull: {
        handler: async (checkpointOrNull, batchSize) => {
          const lastUpdatedAt = checkpointOrNull?.updatedAt;
          const lastUpdatedId = checkpointOrNull?.id;
          const urlSearchParams = new URLSearchParams();
          if (lastUpdatedAt !== undefined) {
            urlSearchParams.set('updated_at', String(lastUpdatedAt));
          }
          if (lastUpdatedId !== undefined) {
            urlSearchParams.set('id', String(lastUpdatedId));
          }
          urlSearchParams.set('batch_size', String(batchSize));
          // TODO(mitch, 2026-02-08): Remove include_features param after old clients have updated
          urlSearchParams.set('include_features', 'true');
          logger.info('Pull latest books ', { lastUpdatedId, lastUpdatedAt, checkpoint: checkpointOrNull });
          const url = urlJoin([getServerBaseUrl(), `${this.endpointPrefix}`, `/pull?${urlSearchParams}`]);
          const res = await this.requestWithAuth(url);
          const data = await res.json();

          logger.info('pull result ', { documents: data.documents.length, checkpoint: data.checkpoint });
          return {
            documents: data.documents,
            checkpoint: data.checkpoint,
          };
        },
        modifier: (data) => {
          this.upsertTransformers(data);
          return data;
        },
        batchSize: 100,
      },
      ...params,
    });
    this.replicationState.error$.subscribe((error) => {
      // check if we are offline
      let isOfflineError = false;
      const { parameters } = error;
      if (parameters) {
        for (const error of parameters.errors ?? []) {
          if (error.name === ReadwiseFetchNetworkConnectivityError.name || error.message === 'Network request failed') {
            isOfflineError = true;
            break;
          }
        }
      }
      // Dont bother blocking the app just because replication failed
      foreground.updateCurrentOfflineTrackedBooksLoadingState(OfflineTrackedBooksLoadingState.Done);
      if (isOfflineError) {
        return;
      }
      logger.error(error.message);
      exceptionHandler.captureException(error.message, {
        extra: {
          rxdbError: error,
          collectionName: this.collection.name,
        },
      });
    });

    from(this.replicationState.awaitInitialReplication())
      .subscribe(async () => {
        // initial replication finished (first full pull+push)
        foreground.updateCurrentOfflineTrackedBooksLoadingState(OfflineTrackedBooksLoadingState.Done);
      });
    return this.replicationState;
  }


  resync(): void {
    if (!this.replicationState) {
      throw new Error('Replication state not initialized. Call init() first.');
    }
    return this.replicationState.reSync();
  }

}
